/**
 * Who a supplier invoice screen, query, draft, review and pending write belongs to (batch 8 T12c3, #3033).
 *
 * Everything the matching screens hold (cached answers, a draft, a displayed review, a request whose outcome is unknown) belongs to
 * ONE signed-in session of one user in one tenant, with the permissions that session holds at this moment:
 *
 *  - `owner` = tenant + user + `sessionFamilyId`: the persistent identity of a pending write. It survives a reload and a token
 *    rotation and does not survive a new login, a logout or another tenant or user.
 *  - `epoch` (`sessionEpoch`) is the in-memory generation. It is not persisted; it only fences work in flight in this page.
 *  - Permissions are read as primitives (never the stable `hasPermission` function) so a downgrade re-renders in the same pass, and
 *    again from the store at the moment of every dispatch and every late answer. The invoice API needs `reports.view_financial`
 *    on every route, the matching grants on top: nothing financial is fetched, stored or shown without them.
 *  - A locked portal is not active: nothing is read or sent while the lock overlay is up.
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';

export const INVOICE_READ_PERMISSIONS: readonly string[] = ['purchase_orders.read', 'purchase_orders.match'];
export const INVOICE_MATCH_PERMISSION = 'purchase_orders.match';
export const INVOICE_OVERRIDE_PERMISSION = 'purchase_orders.match_override';
export const INVOICE_FINANCIAL_PERMISSION = 'reports.view_financial';

/** What an operation needs: `read` (list, detail, facts), `match` (preview, create, reject), `override` (accept; reject of an accepted one). */
export type InvoiceRequirement = 'read' | 'match' | 'override';

export const LEGACY_FAMILY = 'legacy-session';

export interface InvoiceOwner {
  readonly tenantId: string;
  readonly userId: string;
  readonly sessionFamilyId: string;
}

export interface InvoiceAccess {
  /** Identity of the signed-in session (also while locked). `null`: nobody is signed in. */
  readonly owner: InvoiceOwner | null;
  readonly epoch: number;
  /** Signed in and not locked: the only state in which anything may be read or sent. */
  readonly active: boolean;
  /** `reports.view_financial` AND (`purchase_orders.read` OR `purchase_orders.match`). */
  readonly canRead: boolean;
  /** `purchase_orders.match` AND `reports.view_financial`: preview, create, reject. */
  readonly canMatch: boolean;
  /** `canMatch` AND `purchase_orders.match_override`: accept a variance, reject an accepted invoice. */
  readonly canOverride: boolean;
}

export const ownerEquals = (a: InvoiceOwner | null, b: InvoiceOwner | null): boolean =>
  a !== null && b !== null && a.tenantId === b.tenantId && a.userId === b.userId && a.sessionFamilyId === b.sessionFamilyId;

/** A string that is equal exactly for equal owners. */
export const ownerId = (owner: InvoiceOwner): string => JSON.stringify([owner.tenantId, owner.userId, owner.sessionFamilyId]);

type AuthSnapshot = ReturnType<typeof useAuthStore.getState>;

function buildOwner(tenantId: unknown, userId: unknown, family: unknown, authenticated: boolean): InvoiceOwner | null {
  if (!authenticated) return null;
  if (typeof tenantId !== 'string' || tenantId === '' || typeof userId !== 'string' || userId === '') return null;
  return { tenantId, userId, sessionFamilyId: typeof family === 'string' && family !== '' ? family : LEGACY_FAMILY };
}

interface Holds {
  read: boolean;
  match: boolean;
  override: boolean;
  financial: boolean;
}

function buildAccess(owner: InvoiceOwner | null, epoch: number, locked: boolean, holds: Holds): InvoiceAccess {
  const active = owner !== null && !locked;
  const canRead = active && holds.financial && (holds.read || holds.match);
  const canMatch = active && holds.financial && holds.match;
  return { owner, epoch, active, canRead, canMatch, canOverride: canMatch && holds.override };
}

const holdsOf = (state: AuthSnapshot): Holds => ({
  read: state.hasPermission(INVOICE_READ_PERMISSIONS[0] as string),
  match: state.hasPermission(INVOICE_MATCH_PERMISSION),
  override: state.hasPermission(INVOICE_OVERRIDE_PERMISSION),
  financial: state.hasPermission(INVOICE_FINANCIAL_PERMISSION),
});

/** The access of the store as it is at this moment. Use it at the moment of an action, never a captured copy. */
export function readInvoiceAccess(state: AuthSnapshot = useAuthStore.getState()): InvoiceAccess {
  const owner = buildOwner(state.user?.tenantId, state.user?.id, state.sessionFamilyId, state.isAuthenticated);
  return buildAccess(owner, state.sessionEpoch, state.isLocked, holdsOf(state));
}

/** The reactive access. Every input is a primitive, so an owner or permission change re-renders in the same pass. */
export function useInvoiceAccess(): InvoiceAccess {
  const tenantId = useAuthStore((state) => state.user?.tenantId ?? null);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const family = useAuthStore((state) => state.sessionFamilyId);
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const locked = useAuthStore((state) => state.isLocked);
  const epoch = useAuthStore((state) => state.sessionEpoch);
  const read = useAuthStore((state) => state.hasPermission('purchase_orders.read'));
  const match = useAuthStore((state) => state.hasPermission(INVOICE_MATCH_PERMISSION));
  const override = useAuthStore((state) => state.hasPermission(INVOICE_OVERRIDE_PERMISSION));
  const financial = useAuthStore((state) => state.hasPermission(INVOICE_FINANCIAL_PERMISSION));
  return useMemo(
    () => buildAccess(buildOwner(tenantId, userId, family, authenticated), epoch, locked, { read, match, override, financial }),
    [tenantId, userId, family, authenticated, locked, epoch, read, match, override, financial],
  );
}

/**
 * Identity of everything one signed-in owner may see and do: owner, generation and rights. Used as a React `key`, so a different
 * owner, a new login of the same user or any change of rights remounts the subtree in the very render that sees it.
 */
export function accessKey(access: InvoiceAccess): string {
  if (access.owner === null || !access.active) return 'none';
  return JSON.stringify([access.owner.tenantId, access.owner.userId, access.owner.sessionFamilyId, access.epoch, access.canRead, access.canMatch, access.canOverride]);
}

export const holdsRequirement = (access: InvoiceAccess, requirement: InvoiceRequirement): boolean =>
  requirement === 'read' ? access.canRead : requirement === 'match' ? access.canMatch : access.canOverride;

// ---------------------------------------------------------------------------
// Refusals and fences
// ---------------------------------------------------------------------------

/** Refused before any request: the caller lacks what the endpoint needs. */
export class InvoiceAccessError extends Error {
  constructor(readonly requirement: InvoiceRequirement | 'session') {
    super(`Missing permission: ${requirement}`);
    this.name = 'InvoiceAccessError';
  }
}

/** Refused before any request: the session, tenant, user, generation or target is no longer the one the action began under. */
export class OwnerChangedError extends Error {
  constructor() {
    super('The session changed before the request was sent');
    this.name = 'OwnerChangedError';
  }
}

export type InvoiceOutcome<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: unknown };

/**
 * A request WAS sent and its answer arrived after the session, tenant, user, generation, a needed permission or the target had
 * changed. It must not reach the screen, the cache or a notification; it is carried only so the owner of a keyed write can learn
 * what the server decided.
 */
export class LateResultError extends Error {
  constructor(readonly outcome: InvoiceOutcome<unknown>) {
    super('The result arrived after the session changed');
    this.name = 'LateResultError';
  }
}

export const isLocalRefusal = (error: unknown): boolean => error instanceof InvoiceAccessError || error instanceof OwnerChangedError;
/** Errors a screen must swallow silently: they concern an owner or target that is no longer on screen. */
export const isSessionCancel = (error: unknown): boolean => error instanceof OwnerChangedError || error instanceof LateResultError;

/** What an in-flight action started under, checked again before it may change anything. */
export interface InvoiceFence {
  readonly owner: InvoiceOwner;
  readonly epoch: number;
  readonly requirement: InvoiceRequirement;
  /** The purchase order or invoice the action concerns; `null` for an action without one. */
  readonly target: string | null;
  /** True while the screen that began the action still shows `target` (a work-order switch makes it false). */
  readonly targetCurrent: () => boolean;
}

export interface FenceRequest {
  readonly owner: InvoiceOwner | null;
  readonly epoch: number;
  readonly target?: string | null;
  readonly targetCurrent?: () => boolean;
}

/**
 * The fence of NOW, for an action that began under `expected`. Throws before anything is sent when that owner or generation is not
 * the live one, the target is no longer shown, or a needed permission is no longer held.
 */
export function takeFence(expected: FenceRequest, requirement: InvoiceRequirement): InvoiceFence {
  if (expected.owner === null) throw new InvoiceAccessError('session');
  const live = readInvoiceAccess();
  const targetCurrent = expected.targetCurrent ?? (() => true);
  if (!live.active || live.owner === null || !ownerEquals(live.owner, expected.owner) || live.epoch !== expected.epoch || !targetCurrent()) {
    throw new OwnerChangedError();
  }
  if (!holdsRequirement(live, requirement)) throw new InvoiceAccessError(requirement);
  return { owner: live.owner, epoch: live.epoch, requirement, target: expected.target ?? null, targetCurrent };
}

/** Whether what a fence was taken under still holds: same owner, generation, unlocked, permission still held, target still shown. */
export function fenceHolds(fence: InvoiceFence): boolean {
  const live = readInvoiceAccess();
  return live.active && ownerEquals(live.owner, fence.owner) && live.epoch === fence.epoch && holdsRequirement(live, fence.requirement) && fence.targetCurrent();
}

/** Same owner and generation (permissions and target ignored): whose answer this is, for a write that already left. */
export function sameSession(fence: InvoiceFence): boolean {
  const live = readInvoiceAccess();
  return live.active && ownerEquals(live.owner, fence.owner) && live.epoch === fence.epoch;
}

// ---------------------------------------------------------------------------
// Cache scope
// ---------------------------------------------------------------------------

export function ownerSegments(owner: InvoiceOwner): string[] {
  return [owner.tenantId, owner.userId, owner.sessionFamilyId];
}
